import { Moon, ShieldCheck, Sun } from "lucide-react";
import { Link } from "react-router-dom";

import { Avatar, Kbd } from "@m2s/design-system";

import { NotificationBell } from "@/components/NotificationBell";
import { useIsMobile } from "@/hooks/useIsMobile";
import { api, setToken } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import { resolveTheme, useUIStore } from "@/store/ui";

export function Topbar() {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const clear = useAuthStore((s) => s.clear);
  const setPaletteOpen = useUIStore((s) => s.setPaletteOpen);
  const setMobileNavOpen = useUIStore((s) => s.setMobileNavOpen);
  const theme = useUIStore((s) => s.theme);
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const isMobile = useIsMobile();
  const isDark = resolveTheme(theme) === "dark";

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    clear();
    window.location.assign("/login");
  };

  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: isMobile ? 8 : 12,
        height: 48,
        padding: isMobile ? "0 12px" : "0 20px",
        borderBottom: "1px solid var(--hairline)",
        background: "var(--canvas)",
        position: "sticky",
        top: 0,
        zIndex: 5,
      }}
    >
      {isMobile && (
        <button
          type="button"
          aria-label="Open menu"
          onClick={() => setMobileNavOpen(true)}
          style={{
            width: 36,
            height: 36,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            cursor: "pointer",
            color: "var(--ink)",
            padding: 0,
          }}
        >
          {/* Three horizontal lines — pure CSS, no icon dependency. */}
          <span aria-hidden style={{ display: "inline-flex", flexDirection: "column", gap: 4 }}>
            <span style={hamburgerLine} />
            <span style={hamburgerLine} />
            <span style={hamburgerLine} />
          </span>
        </button>
      )}

      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          // Cap the workspace name on mobile so it doesn't push other
          // controls off-screen.
          maxWidth: isMobile ? 140 : "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {isSuperAdmin ? (
          <>
            <span style={{ color: "var(--ink)" }}>Super admin</span>
            {!isMobile && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  color: "var(--mute)",
                  marginLeft: 8,
                }}
              >
                minute2sec
              </span>
            )}
          </>
        ) : (
          tenant?.name ?? "—"
        )}
      </span>

      {/* Search is a desktop-only affordance — on mobile screens it would
          push everything else off. ⌘K still works via the keyboard. */}
      {!isMobile && (
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          style={{
            flex: 1,
            maxWidth: 380,
            marginLeft: 16,
            background: "var(--surface-1)",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            padding: "5px 10px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: "var(--mute)",
            fontSize: 13,
            textAlign: "left",
            cursor: "pointer",
          }}
        >
          <span>Search issues, pages, people…</span>
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4 }}>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </button>
      )}

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 8 : 12,
        }}
      >
        <NotificationBell />

        <Link
          to="/settings#mfa"
          aria-label="Two-factor devices"
          title="Manage two-factor devices"
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            color: "var(--body)",
            padding: 0,
            textDecoration: "none",
          }}
        >
          <ShieldCheck size={16} aria-hidden />
        </Link>

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            color: "var(--body)",
            cursor: "pointer",
            padding: 0,
          }}
        >
          {isDark ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
        </button>

        {user && (
          <>
            <Avatar initials={initials(user.display_name)} title={user.display_name} />
            {/* The display name is the first thing we drop on mobile —
                the avatar already identifies the user. */}
            {!isMobile && (
              <span style={{ fontSize: 13, color: "var(--body)" }}>{user.display_name}</span>
            )}
            <button
              type="button"
              onClick={logout}
              style={{
                background: "transparent",
                border: "1px solid var(--hairline)",
                borderRadius: 4,
                padding: isMobile ? "6px 10px" : "5px 10px",
                fontSize: 12,
                color: "var(--body)",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </header>
  );
}

const hamburgerLine: React.CSSProperties = {
  width: 16,
  height: 1.5,
  background: "var(--ink)",
  borderRadius: 1,
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
